import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Switch,
  InputNumber,
  message,
  Tabs,
  Space,
  Tag,
  Divider,
  Alert,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { createWidget, CreateWidgetRequest, WidgetAppearance, WidgetSettings, AuthConfig, LoggingConfig, SourceType, getProjectsForWidget } from '../../lib/widgetsApi';
import { getSsoIntegrations, SsoIntegration } from '../../lib/integrationsApi';

const { TextArea } = Input;
const { TabPane } = Tabs;

interface CreateWidgetModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateWidgetModal: React.FC<CreateWidgetModalProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [integrations, setIntegrations] = useState<SsoIntegration[]>([]);
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [domainInput, setDomainInput] = useState('');
  const [sourceType, setSourceType] = useState<SourceType>('website');
  const [useSsoIntegration, setUseSsoIntegration] = useState(false);

  useEffect(() => {
    if (visible) {
      loadProjects();
      loadIntegrations();
    }
  }, [visible]);

  const loadProjects = async () => {
    try {
      const data = await getProjectsForWidget();
      setProjects(data);
    } catch (error: any) {
      message.error('Failed to load projects');
    }
  };

  const loadIntegrations = async () => {
    try {
      const data = await getSsoIntegrations();
      setIntegrations(data.filter(i => i.isActive));
    } catch (error: any) {
      console.error('Failed to load integrations:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const appearance: WidgetAppearance = {
        primaryColor: values.primaryColor,
        secondaryColor: values.secondaryColor,
        position: values.position,
        size: values.size,
        welcomeMessage: values.welcomeMessage,
        placeholder: values.placeholder,
        autoOpen: values.autoOpen || false,
        showBranding: values.showBranding !== false,
      };

      const settings: WidgetSettings = {
        maxMessagesHistory: values.maxMessagesHistory || 50,
        rateLimitPerMin: values.rateLimitPerMin || 10,
        collectUserInfo: values.collectUserInfo || false,
        enableFeedback: values.enableFeedback !== false,
      };

      // Build authConfig based on source type
      const authConfig: AuthConfig = {
        sourceType: values.sourceType || 'website',
      };

      if (values.sourceType === 'website') {
        authConfig.websiteSettings = {
          allowAnonymous: values.allowAnonymous !== false,
          collectUserInfo: values.collectUserInfo || false,
          userInfoFields: values.userInfoFields || ['name', 'email'],
        };
      } else if (values.sourceType === 'teams') {
        authConfig.teamsSettings = {
          clientId: values.teamsClientId,
          tenantId: values.teamsTenantId || 'common',
          redirectUri: values.teamsRedirectUri,
          scopes: values.teamsScopes || ['User.Read'],
          captureTeamContext: values.captureTeamContext !== false,
          captureChannelContext: values.captureChannelContext !== false,
        };
      } else if (values.sourceType === 'maximo') {
        authConfig.maximoSettings = {
          authUrl: values.maximoAuthUrl,
          apiUrl: values.maximoApiUrl,
          loginType: values.maximoLoginType || 'oauth',
        };
      } else if (values.sourceType === 'slack') {
        authConfig.slackSettings = {
          clientId: values.slackClientId,
          redirectUri: values.slackRedirectUri,
          scopes: values.slackScopes || ['users:read', 'chat:write'],
          captureWorkspaceContext: values.captureWorkspaceContext !== false,
        };
      } else if (values.sourceType === 'simple-auth') {
        authConfig.simpleAuthSettings = {
          providers: values.simpleAuthProviders || ['google', 'email'],
          required: values.simpleAuthRequired !== false,
          googleClientId: values.googleClientId,
          microsoftClientId: values.microsoftClientId,
          collectFields: values.collectFields || ['name', 'email'],
          rememberUser: values.rememberUser !== false,
          allowAnonymous: values.allowAnonymous || false,
        };
      }

      const logging: LoggingConfig = {
        logToConsole: values.logToConsole !== false,
        logLevel: values.logLevel || 'basic',
        includeIPAddress: values.includeIPAddress || false,
        includeUserAgent: values.includeUserAgent || false,
        includeReferrer: values.includeReferrer || false,
      };

      const request: CreateWidgetRequest = {
        name: values.name,
        description: values.description,
        projectId: values.projectId,
        ...(values.ssoIntegrationId && { ssoIntegrationId: values.ssoIntegrationId }), // NEW: Add SSO integration reference
        appearance,
        settings,
        ...(!values.ssoIntegrationId && { authConfig }), // Only include authConfig if not using SSO integration
        logging,
        allowedDomains,
      };

      await createWidget(request);
      message.success('Widget created successfully');
      form.resetFields();
      setAllowedDomains([]);
      setSourceType('website');
      setUseSsoIntegration(false);
      onSuccess();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create widget');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = () => {
    if (domainInput && !allowedDomains.includes(domainInput)) {
      setAllowedDomains([...allowedDomains, domainInput]);
      setDomainInput('');
    }
  };

  const handleRemoveDomain = (domain: string) => {
    setAllowedDomains(allowedDomains.filter((d) => d !== domain));
  };

  return (
    <Modal
      title="Create Chat Widget"
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={700}
      okText="Create Widget"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          sourceType: 'website',
          primaryColor: '#13afe3',
          secondaryColor: '#6b7280',
          position: 'bottom-right',
          size: 'medium',
          welcomeMessage: 'Hi! How can I help you today?',
          placeholder: 'Type your message...',
          autoOpen: false,
          showBranding: true,
          maxMessagesHistory: 50,
          rateLimitPerMin: 10,
          collectUserInfo: false,
          enableFeedback: true,
          allowAnonymous: true,
          logToConsole: true,
          logLevel: 'basic',
          teamsTenantId: 'common',
          captureTeamContext: true,
          captureChannelContext: true,
          maximoLoginType: 'oauth',
          captureWorkspaceContext: true,
        }}
      >
        <Tabs defaultActiveKey="basic">
          <TabPane tab="Basic Info" key="basic">
            <Form.Item
              label="Widget Name"
              name="name"
              rules={[{ required: true, message: 'Please enter widget name' }]}
            >
              <Input placeholder="e.g., Customer Support Chat" />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <TextArea rows={2} placeholder="Optional description" />
            </Form.Item>

            <Form.Item
              label="Project"
              name="projectId"
              rules={[{ required: true, message: 'Please select a project' }]}
            >
              <Select placeholder="Select project">
                {projects.map((project) => (
                  <Select.Option key={project.id} value={project.id}>
                    {project.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Source Type"
              name="sourceType"
              rules={[{ required: true, message: 'Please select source type' }]}
            >
              <Select
                placeholder="Select source type"
                onChange={(value) => setSourceType(value as SourceType)}
              >
                <Select.Option value="website">Website (Anonymous/Optional Login)</Select.Option>
                <Select.Option value="simple-auth">Simple Login (Google/Microsoft/Email) - Just User Info</Select.Option>
                <Select.Option value="teams">Microsoft Teams (SSO Required)</Select.Option>
                <Select.Option value="maximo">Maximo (Login Required)</Select.Option>
                <Select.Option value="slack">Slack (OAuth Required)</Select.Option>
                <Select.Option value="custom-sso">Custom SSO</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Allowed Domains">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Input.Search
                  placeholder="example.com"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onSearch={handleAddDomain}
                  enterButton={<PlusOutlined />}
                />
                <div>
                  {allowedDomains.map((domain) => (
                    <Tag
                      key={domain}
                      closable
                      onClose={() => handleRemoveDomain(domain)}
                    >
                      {domain}
                    </Tag>
                  ))}
                  {allowedDomains.length === 0 && (
                    <span className="text-gray-400 text-sm">
                      All domains allowed (not recommended for production)
                    </span>
                  )}
                </div>
              </Space>
            </Form.Item>
          </TabPane>

          <TabPane tab="Appearance" key="appearance">
            <Form.Item label="Primary Color" name="primaryColor">
              <Input type="color" style={{ width: '100px' }} />
            </Form.Item>

            <Form.Item label="Secondary Color" name="secondaryColor">
              <Input type="color" style={{ width: '100px' }} />
            </Form.Item>

            <Form.Item label="Position" name="position">
              <Select>
                <Select.Option value="bottom-right">Bottom Right</Select.Option>
                <Select.Option value="bottom-left">Bottom Left</Select.Option>
                <Select.Option value="top-right">Top Right</Select.Option>
                <Select.Option value="top-left">Top Left</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Size" name="size">
              <Select>
                <Select.Option value="small">Small</Select.Option>
                <Select.Option value="medium">Medium</Select.Option>
                <Select.Option value="large">Large</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Welcome Message" name="welcomeMessage">
              <Input placeholder="Hi! How can I help you today?" />
            </Form.Item>

            <Form.Item label="Input Placeholder" name="placeholder">
              <Input placeholder="Type your message..." />
            </Form.Item>

            <Form.Item label="Auto-Open Widget" name="autoOpen" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item label="Show ThiranAI Branding" name="showBranding" valuePropName="checked">
              <Switch />
            </Form.Item>
          </TabPane>

          <TabPane tab="Authentication" key="authentication">
            {/* SSO Integration Option */}
            {sourceType !== 'website' && integrations.filter(i => i.provider === sourceType).length > 0 && (
              <div className="mb-4">
                <Form.Item label="Use SSO Integration?" valuePropName="checked">
                  <Switch checked={useSsoIntegration} onChange={setUseSsoIntegration} />
                </Form.Item>

                {useSsoIntegration && (
                  <Form.Item
                    label="Select Integration"
                    name="ssoIntegrationId"
                    rules={[{ required: true, message: 'Please select an integration' }]}
                  >
                    <Select placeholder="Select an SSO integration">
                      {integrations
                        .filter(i => i.provider === sourceType)
                        .map(integration => (
                          <Select.Option key={integration.id} value={integration.id}>
                            {integration.name}
                            {integration.description && ` - ${integration.description}`}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                )}

                {useSsoIntegration && (
                  <Alert
                    message="Using SSO Integration"
                    description="This widget will use the selected SSO integration configuration. No need to configure authentication details manually."
                    type="success"
                    showIcon
                    className="mb-4"
                  />
                )}
              </div>
            )}

            {(!useSsoIntegration || sourceType === 'website') && sourceType === 'website' && (
              <>
                <Alert
                  message="Website Source"
                  description="Users can chat anonymously or optionally provide their information. No sign-in required."
                  type="info"
                  showIcon
                  className="mb-4"
                />
                <Form.Item
                  label="Allow Anonymous Chat"
                  name="allowAnonymous"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label="Collect User Info (Optional)"
                  name="collectUserInfo"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </>
            )}

            {!useSsoIntegration && sourceType === 'teams' && (
              <>
                <Alert
                  message="Microsoft Teams OAuth"
                  description="Users will be redirected to Microsoft login. Configure your Azure AD app credentials below."
                  type="info"
                  showIcon
                  className="mb-4"
                />
                <Form.Item
                  label="Azure AD Client ID"
                  name="teamsClientId"
                  rules={[{ required: true, message: 'Client ID is required for Teams' }]}
                >
                  <Input placeholder="00000000-0000-0000-0000-000000000000" />
                </Form.Item>
                <Form.Item
                  label="Tenant ID"
                  name="teamsTenantId"
                >
                  <Input placeholder="common (or your tenant ID)" />
                </Form.Item>
                <Form.Item
                  label="Redirect URI"
                  name="teamsRedirectUri"
                  rules={[{ required: true, message: 'Redirect URI is required' }]}
                >
                  <Input placeholder="https://yourdomain.com/widget" />
                </Form.Item>
                <Form.Item
                  label="Capture Team Context"
                  name="captureTeamContext"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label="Capture Channel Context"
                  name="captureChannelContext"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </>
            )}

            {!useSsoIntegration && sourceType === 'maximo' && (
              <>
                <Alert
                  message="Maximo Authentication"
                  description="Configure Maximo authentication endpoint for user login."
                  type="info"
                  showIcon
                  className="mb-4"
                />
                <Form.Item
                  label="Maximo Auth URL"
                  name="maximoAuthUrl"
                  rules={[{ required: true, message: 'Auth URL is required' }]}
                >
                  <Input placeholder="https://maximo.company.com/auth" />
                </Form.Item>
                <Form.Item
                  label="Maximo API URL"
                  name="maximoApiUrl"
                >
                  <Input placeholder="https://maximo.company.com/api" />
                </Form.Item>
                <Form.Item
                  label="Login Type"
                  name="maximoLoginType"
                >
                  <Select>
                    <Select.Option value="oauth">OAuth</Select.Option>
                    <Select.Option value="basic">Basic Auth</Select.Option>
                    <Select.Option value="form">Form-Based</Select.Option>
                  </Select>
                </Form.Item>
              </>
            )}

            {!useSsoIntegration && sourceType === 'slack' && (
              <>
                <Alert
                  message="Slack OAuth"
                  description="Users will authenticate via Slack OAuth. Configure your Slack app credentials."
                  type="info"
                  showIcon
                  className="mb-4"
                />
                <Form.Item
                  label="Slack Client ID"
                  name="slackClientId"
                  rules={[{ required: true, message: 'Client ID is required' }]}
                >
                  <Input placeholder="00000000000.00000000000" />
                </Form.Item>
                <Form.Item
                  label="Redirect URI"
                  name="slackRedirectUri"
                  rules={[{ required: true, message: 'Redirect URI is required' }]}
                >
                  <Input placeholder="https://yourdomain.com/widget" />
                </Form.Item>
                <Form.Item
                  label="Capture Workspace Context"
                  name="captureWorkspaceContext"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </>
            )}

            {!useSsoIntegration && sourceType === 'simple-auth' && (
              <>
                <Alert
                  message="Simple Login - User Information Only"
                  description="Collect basic user information (name, email) without accessing user's data. Perfect for conversation scoping in multi-tenant scenarios."
                  type="info"
                  showIcon
                  className="mb-4"
                />

                <Form.Item
                  label="Authentication Providers"
                  name="simpleAuthProviders"
                  initialValue={['google', 'email']}
                  rules={[{ required: true, message: 'Select at least one provider' }]}
                  tooltip="Choose which login methods to offer users"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select providers"
                    options={[
                      { label: 'Google Sign-In', value: 'google' },
                      { label: 'Microsoft Sign-In', value: 'microsoft' },
                      { label: 'Email/Password', value: 'email' },
                      { label: 'Anonymous (Session-based)', value: 'anonymous' }
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.simpleAuthProviders !== currentValues.simpleAuthProviders
                  }
                >
                  {({ getFieldValue }) => {
                    const providers = getFieldValue('simpleAuthProviders') || [];
                    return (
                      <>
                        {providers.includes('google') && (
                          <Form.Item
                            label="Google Client ID"
                            name="googleClientId"
                            rules={[{ required: true, message: 'Google Client ID is required' }]}
                            tooltip="Get this from Google Cloud Console > APIs & Services > Credentials"
                          >
                            <Input placeholder="123456789012-abcdefghijklmnop.apps.googleusercontent.com" />
                          </Form.Item>
                        )}

                        {providers.includes('microsoft') && (
                          <Form.Item
                            label="Microsoft Client ID"
                            name="microsoftClientId"
                            rules={[{ required: true, message: 'Microsoft Client ID is required' }]}
                            tooltip="Get this from Azure Portal > App Registrations > Application (client) ID"
                          >
                            <Input placeholder="12345678-1234-1234-1234-123456789012" />
                          </Form.Item>
                        )}
                      </>
                    );
                  }}
                </Form.Item>

                <Form.Item
                  label="User Fields to Collect"
                  name="collectFields"
                  initialValue={['name', 'email']}
                  rules={[{ required: true, message: 'Select at least one field' }]}
                  tooltip="Information to collect from users during login"
                >
                  <Select
                    mode="multiple"
                    placeholder="Select fields"
                    options={[
                      { label: 'Full Name', value: 'name' },
                      { label: 'Email Address', value: 'email' },
                      { label: 'Phone Number', value: 'phone' },
                      { label: 'Company/Organization', value: 'company' }
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  label="Login Required"
                  name="simpleAuthRequired"
                  valuePropName="checked"
                  initialValue={true}
                  tooltip="If enabled, users must login before chatting. If disabled, users can optionally login for personalized experience."
                >
                  <Switch />
                </Form.Item>

                <Form.Item
                  label="Remember User"
                  name="rememberUser"
                  valuePropName="checked"
                  initialValue={true}
                  tooltip="Remember logged-in users across browser sessions"
                >
                  <Switch />
                </Form.Item>

                <Form.Item
                  label="Allow Anonymous Chat"
                  name="allowAnonymous"
                  valuePropName="checked"
                  initialValue={false}
                  tooltip="Allow users to chat without logging in (conversations scoped by session)"
                >
                  <Switch />
                </Form.Item>
              </>
            )}

            <Divider />

            <h3 className="text-base font-semibold mb-3">Logging Configuration</h3>
            <Form.Item
              label="Log to Console"
              name="logToConsole"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label="Log Level"
              name="logLevel"
            >
              <Select>
                <Select.Option value="none">None</Select.Option>
                <Select.Option value="basic">Basic</Select.Option>
                <Select.Option value="detailed">Detailed</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Include IP Address"
              name="includeIPAddress"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label="Include User Agent"
              name="includeUserAgent"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item
              label="Include Referrer"
              name="includeReferrer"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </TabPane>

          <TabPane tab="Settings" key="settings">
            <Form.Item label="Max Message History" name="maxMessagesHistory">
              <InputNumber min={10} max={200} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label="Rate Limit (per minute)" name="rateLimitPerMin">
              <InputNumber min={1} max={60} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="Collect User Information"
              name="collectUserInfo"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>

            <Form.Item label="Enable Feedback" name="enableFeedback" valuePropName="checked">
              <Switch />
            </Form.Item>
          </TabPane>
        </Tabs>
      </Form>
    </Modal>
  );
};

export default CreateWidgetModal;
